import React, { createContext, useContext, useEffect, useState } from 'react'
import type { OfferWrapper, Provider, Result, ResultFilters } from 'generated/api'
import {
	createDataWrapper,
	createLoadingDataWrapper,
	createStaticDataWrapper
} from 'components/patterns/suspense/internals/DataWrapper'
import { pollForResults } from 'experiences/search/utils/utils'
import { isServer, Optional, wait } from 'lib'
import { UnknownProviderError } from 'shared/ResultsProvider/errors'
import { getResultsStatus, ResultsStates } from 'experiences/search/utils/lib'
import { Callback } from 'types'
import { trackOfferClick } from 'experiences/search/Results/services/analytics'
import { DataWrapper } from 'components/patterns/suspense/internals/types'
import { addDDTiming } from 'services/AnalyticsLite/Datadog/events'
import { isPromise } from 'lib/types/object'

type OfferRedirectFn = (offer: OfferWrapper) => void

interface ResultsContextValues {
	results: DataWrapper<Result>
	filters: DataWrapper<ResultFilters>
	providers: DataWrapper<Provider[]>
	supportsFilters: DataWrapper<ResultsStates>
	redirectToOffer: OfferRedirectFn
}

const redirectToOffer = (offer: OfferWrapper) => {
	Promise.race([wait(300), trackOfferClick(offer)]).then(() => {
		window.location.href = offer.url
	})
}

const ResultsContext = createContext<ResultsContextValues>({
	results: createLoadingDataWrapper(),
	filters: createLoadingDataWrapper(),
	providers: createLoadingDataWrapper(),
	supportsFilters: createLoadingDataWrapper(),
	redirectToOffer: redirectToOffer
})

export const useOfferRedirect = (): OfferRedirectFn => {
	const { redirectToOffer } = useContext(ResultsContext)
	return redirectToOffer
}

const getSearchResults = (id?: string): Promise<Result> => pollForResults(id!)

type SearchProps =
	| {
			searchId: string
			results?: undefined
	  }
	| {
			results?: Result
			searchId?: undefined
	  }

type Props = SearchProps & { redirectOverride?: Callback<OfferWrapper> } & { datawrapperOverride?: DataWrapper<Result> }

export const ResultsProvider: React.FC<React.PropsWithChildren<Props>> = ({
	children,
	searchId,
	results,
	redirectOverride,
	datawrapperOverride
}) => {
	let resultsWrapper: DataWrapper<Result>
	if (isServer()) {
		resultsWrapper = createLoadingDataWrapper<Result>()
	} else if (datawrapperOverride) {
		resultsWrapper = datawrapperOverride
	} else if (searchId) {
		resultsWrapper = createDataWrapper(getSearchResults, searchId)
		resultsWrapper.fork(result => {
			if (result.cnfRedirect && typeof window !== 'undefined') window.location.href = result.cnfRedirect
		})
	} else if (results) {
		resultsWrapper = createStaticDataWrapper(results)
	} else {
		resultsWrapper = createLoadingDataWrapper<Result>()
	}

	const value: ResultsContextValues = {
		results: resultsWrapper,
		filters: resultsWrapper.fork(result => result.filters!),
		providers: resultsWrapper.fork(result => result.providers),
		supportsFilters: resultsWrapper.fork(getResultsStatus),
		redirectToOffer: redirectOverride || redirectToOffer
	}
	return <ResultsContext.Provider value={value}>{children}</ResultsContext.Provider>
}

export const useResults = (): Result => {
	const { results } = useContext(ResultsContext)
	return results.read()
}

export const useSearchId = (): string => {
	const { results } = useContext(ResultsContext)
	return results.getId()
}

export const useProviders = (): Array<Provider> => {
	const { providers } = useContext(ResultsContext)
	return providers.read()
}

export const useProvider = (uuid: string): Provider => {
	const providers = useProviders()
	const provider = providers.find(provider => provider.uuid === uuid)
	if (provider) return provider
	throw new UnknownProviderError()
}

export const useResultsState = (): Optional<ResultsStates> => {
	const { supportsFilters } = useContext(ResultsContext)
	const [state, setState] = useState<Optional<ResultsStates>>(undefined)
	useEffect(() => {
		try {
			const value = supportsFilters.read()
			setState(value)
		} catch (err) {
			if (isPromise(err)) {
				const error = err as Promise<ResultsStates>
				error.then((value: ResultsStates) => {
					addDDTiming('filterable_results_loaded')
					setState(value)
				})
			} else {
				throw err
			}
		}
	}, [supportsFilters])
	return state
}
